import { useCallback, useEffect, useRef } from "react";

import { ampli } from "@hl/shared-features/lib/ampli";
import {
  TransactionStateType,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import {
  isTestnetNetwork,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";

import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "./context";

export const DeployFinishTracking = ({ txnId }: { txnId: string }) => {
  const {
    state: { draftId },
  } = useCreateCollectionState();
  const transactionState = useTransactionState(txnId);
  const form = useCreateCollectionFormContext();
  const { collection, collectionType } = form.values;

  const trackedState = useRef<string | null>(null);
  const stateRef = useRef(transactionState);

  const recordFinishEvent = useCallback(() => {
    if (!ampli.isLoaded) {
      return;
    }
    const state = stateRef.current;
    if (!state?.type) {
      return;
    }
    console.log("deploy finish event", { txStateType: state.type });
    const network = networkLookup(collection.chainId);

    ampli.deployCollectionFinish({
      draftId: draftId!,
      projectName: collection.name!,
      collectionType,
      blockchain: network.type,
      isTestnet: isTestnetNetwork(network.type),
      txStateType: state.type,
      status: [
        TransactionStateType.Done,
        TransactionStateType.WaitingConfirmedTx,
      ].includes(state.type)
        ? "success"
        : "error",
      errorMessage: state?.error?.details ?? state.type,
    });
  }, [stateRef, collection]);

  // track deploy finish
  useEffect(() => {
    stateRef.current = transactionState;
    const txStateType = transactionState?.type;
    if (!txStateType) {
      return;
    }

    if (
      [
        TransactionStateType.WaitingWeb2Update,
        TransactionStateType.WaitingPrepareTx,
        TransactionStateType.WaitingSignedTx,
      ].includes(txStateType)
    ) {
      // pending state
      return;
    }
    trackedState.current = `${txnId}-${txStateType}`;
    recordFinishEvent();
  }, [stateRef, transactionState, recordFinishEvent]);

  useEffect(() => {
    return () => {
      const state = stateRef.current;
      if (state?.type && trackedState.current !== `${txnId}-${state.type}`) {
        // unmounting and in pending sate, record finish event with current state
        recordFinishEvent();
      }
    };
  }, [stateRef, txnId]);

  // // detecting if user leaving the page via page reload/close
  useEffect(() => {
    const handleUnload = () => {
      const state = stateRef.current;
      if (state?.type && trackedState.current !== `${txnId}-${state.type}`) {
        // closing winding in pending state, record finish as failure
        recordFinishEvent();
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [stateRef, txnId, recordFinishEvent]);

  return null;
};
